/**
 * Lightweight scroll-reveal primitive for the public site.
 *
 * ▶ Design goals
 *   • Zero JS animation loops — the observer only *toggles a class*.
 *     All motion happens in CSS via `transform` + `opacity` (GPU-composited).
 *   • Runs once per element (unobserve after reveal) so it never pins the
 *     main thread on subsequent scrolls.
 *   • Honours `prefers-reduced-motion: reduce` — reveals instantly with
 *     no transform for users who ask.
 *   • Tiny: no third-party motion library.
 *
 * ▶ Usage
 *   <Reveal>
 *     <section>…</section>
 *   </Reveal>
 *
 *   <Reveal delay={120} y={16}>
 *     <div className="card">…</div>
 *   </Reveal>
 *
 * ▶ NOT for admin/driver dashboards — do not import there.
 */
import { cloneElement, useEffect, useRef, useState } from "react";

const REDUCED =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function useReveal({ threshold = 0.12, rootMargin = "0px 0px -40px 0px" } = {}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(REDUCED); // reduced motion → skip animation
  useEffect(() => {
    if (shown || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        }
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, threshold, rootMargin]);
  return { ref, shown };
}

/**
 * Wrap any element to fade+rise it in on scroll.
 * `y` = pixels the element rises through (default 14).
 * `delay` = ms delay after entering the viewport (default 0).
 */
export function Reveal({ children, y = 14, delay = 0, className = "", ...rest }) {
  const { ref, shown } = useReveal();
  const style = {
    transform: shown ? "none" : `translate3d(0, ${y}px, 0)`,
    opacity: shown ? 1 : 0,
    transition: `opacity 620ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms, transform 620ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms`,
    willChange: shown ? "auto" : "opacity, transform",
  };
  const only = Array.isArray(children) ? children[0] : children;
  if (only && typeof only === "object" && only.type) {
    // Clone the single child so we don't add an extra DOM node → keeps the
    // existing layout / Tailwind classes intact.
    return cloneElement(only, {
      ref,
      style: { ...(only.props.style || {}), ...style },
      className: `${only.props.className || ""} ${className}`.trim(),
      ...rest,
    });
  }
  return (
    <div ref={ref} style={style} className={className} {...rest}>
      {children}
    </div>
  );
}
